import { ContentBox } from '@/common/layout/ContentBox';
import { far, fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { merge } from 'highcharts';
import { useEffect, useState } from 'react';
import { Banner } from '../banner/Banner';
import { ExportButton } from '../buttons/ExportButton';
import { TitleButton } from '../buttons/TitleButton';
import { Form } from '../form/Form';
import { FormFields } from '../form/rendering/FormFields';
import { useNavStateSync } from '../hooks/navState/useNavStateSync';
import { useModuleFromRoute } from '../hooks/route/useModuleFromRoute';
import { useNotif } from '../notif/NotifContext';
import { usePrincipal } from '../principal/PrincipalContext';
import { useRsrc } from '../rsrc/RsrcContext';
import { Title } from '../title/Title';
import { ExprUtils } from '../utils/ExprUtils';
import { DashboardContextProvider } from './DashboardContext';
import { FilterContainer } from './FilterContainer';

export const Dashboard = ({
  title,
  titleIcon,
  children,
  fieldDefs,
  defaultFormValues,
  augButtons,
  onReset,
  entity = 'MCD_INV',
}) => {
  const [formValues, setFormValues] = useNavStateSync('formValues', defaultFormValues);
  const [chartRefs, setChartRefs] = useState();
  const [exportJobs, setExportJobs] = useState([]);

  const rsrc = useRsrc();
  const module = useModuleFromRoute();
  const { principal } = usePrincipal();
  const { notifs } = useNotif();

  // Exports launched from a dashboard are not tied to a specific entity instance
  const isDashboardExportJob = (job) =>
    job.module === module && job.entity === entity && job.entityId == null && job.cat === 'EXPORT';

  useEffect(() => {
    const rowsReq = {
      expr: ExprUtils.whereParams(
        'module',
        module,
        'entity',
        entity,
        'entityId',
        null,
        'cat',
        'EXPORT',
        'status',
        'RUN',
        'recBy',
        principal.username,
      ),
    };
    rsrc.rows('skdr', 'JOB', rowsReq).then(setExportJobs);
  }, [module, entity]);

  useEffect(() => {
    if (!notifs || notifs.length === 0) return;
    setExportJobs((prevJobs) => {
      let updatedJobs = [...prevJobs];
      notifs.forEach((notif) => {
        const job = notif.event?.job;
        if (job && isDashboardExportJob(job) && !updatedJobs.some((j) => j.id === job.id)) {
          updatedJobs.push(job);
        }
      });
      return updatedJobs.filter(
        (j) => !notifs.some((notif) => notif.event?.job?.id === j.id && notif.eventType.startsWith('FINISH')),
      );
    });
  }, [notifs]);

  const exportReq = () => {
    let chartImages = [];
    chartRefs
      .filter((chart) => chart.current)
      .forEach((chartRef, idx) => {
        let chart = chartRef.current.chart;
        let options = chart.getOptions();
        let exportOptions = options.exporting?.chartOptions || {};
        let finalOptions = merge(options, exportOptions);

        let svg = chart.getSVG(finalOptions);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);

          const pngBase64 = canvas.toDataURL('image/png');
          chartImages[idx] = pngBase64;
          return pngBase64;
        };

        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
      });

    return {
      uiType: 'DASHBOARD',
      entity,
      chartImages,
    };
  };

  const resetFilters = () => {
    setFormValues(defaultFormValues);
    if (onReset) {
      onReset();
    }
  };

  const buttons = [];
  if (augButtons) {
    buttons.push(...augButtons);
  }
  buttons.push(
    <TitleButton key="resetViewButton" icon={far.faWindowRestore} toolTip="Reset Filters" onClick={resetFilters} />,
    <ExportButton key="exportButton" exportReq={exportReq} />,
  );

  return (
    <DashboardContextProvider setChartRefs={setChartRefs}>
      <Form formValues={formValues} setFormValues={setFormValues}>
        <ContentBox>
          {/* Title */}
          <Title icon={titleIcon} title={title} buttons={buttons} />
          {/* Running Exports */}
          {exportJobs.map((job) => (
            <Banner key={job.id} icon={fas.faExclamationTriangle} text={'Running Export ' + job.name} />
          ))}
          {/* Optional Filters from fieldDefs */}
          {fieldDefs && <FormFields fieldDefs={fieldDefs} container={FilterContainer} />}

          {/* Main Dashboard Content */}
          {children}
        </ContentBox>
      </Form>
    </DashboardContextProvider>
  );
};
